import cookie from 'js-cookie'
import {
  BrazeInAppConfiguration,
  SegmentEventData,
  EventProcessingResult,
  EventProperties,
  BrazeEventAttributes
} from '../types'

/**
 * Filters event properties based on allowed properties list
 * @param allowedProperties - Array of allowed property keys
 * @param properties - Event payload to filter
 * @returns Filtered event properties
 */
export const filterEventProperties = (
  allowedProperties: string[],
  properties: EventProperties
): EventProperties => {
  const filteredEvent: EventProperties = {}
  const allowedPropertiesSet = new Set(allowedProperties)

  for (const [key, value] of Object.entries(properties)) {
    if (allowedPropertiesSet.has(key)) {
      filteredEvent[key] = value
    }
  }

  return filteredEvent
}

/**
 * Processes event properties for Braze tracking based on consent and configuration
 * @param eventData - Segment event data
 * @param inAppConfiguration - Optional Braze in-app messaging configuration
 * @returns Processed event result with event name and attributes
 */
export const processEventProperties = (
  eventData: SegmentEventData,
  inAppConfiguration?: BrazeInAppConfiguration
): EventProcessingResult | null => {
  const { event, userId, source, trackId, anonymousId, properties } = eventData

  let trackingConsented = false
  try {
    trackingConsented = cookie?.get('trackingConsented') === 'true'
  } catch (error) {
    console.warn('Error accessing tracking consent cookie:', error)
    trackingConsented = false
  }

  // If user has consented to tracking or no configuration is provided, track everything
  if (trackingConsented || !inAppConfiguration) {
    const attr: BrazeEventAttributes = {
      event,
      source,
      userId,
      trackId,
      anonymousId,
      ...properties
    }

    return { event, attr }
  }

  const {
    eventsToTurnGeneric = [],
    genericEventName = 'Generic Event',
    allowedProperties = [],
    allowedEvents = []
  } = inAppConfiguration

  const eventsToTurnGenericSet = new Set(eventsToTurnGeneric)
  const allowedEventsSet = new Set(allowedEvents)

  if (eventsToTurnGenericSet.has(event)) {
    return {
      event: genericEventName,
      attr: {
        event: genericEventName,
        source,
        userId,
        trackId,
        anonymousId
      }
    }
  }

  if (allowedEventsSet.has(event)) {
    const filteredEventProperties = filterEventProperties(
      allowedProperties,
      properties
    )
    const attr: BrazeEventAttributes = {
      event,
      source,
      userId,
      trackId,
      anonymousId,
      ...filteredEventProperties
    }
    return { event, attr }
  }

  // Block event entirely
  return null
}
